<template>
    <div>
        <div class="bg-cover flex items-center hero_section bg_navy_blue">
			<div class="container">
				<div class="row lg:items-center">
				<div class="lg:col-5/7">
             		<h1 class="font-semibold">{{ page.title }}</h1>
					<h2 class="font-light">{{ page.sub_title }}</h2>
				</div>
				<div class="lg:col-2/7">
					<div class="right_image">
					<img alt="" class="max-h-full mmcts_image w-100" src="/images/MMCTS_cover-image.png">
					</div>
				</div>
			</div>
		</div>
        </div>

        <div class="container space-y-8 my-8">
            <p class="text-sm">
                <span class="italic">MMCTS</span> brings online training for cardio-thoracic surgeons to an
                entirely new level with step-by-step video demonstrations of surgical procedures, supported by succinct
                text and clear graphics. It is published as a free service by the
                <a class="text-primary" href="http://www.eacts.org/" rel="noopener" target="_blank">
                    European Association for Cardio-Thoracic Surgery
                </a>.
            </p>
            
            <!--Editor's Choice Section-->
            <template v-if="editorchoice" >
                 <h3 class="mr-4 text-3xl font-light uppercase pt-8 border-t border-t-grey-light" style="margin-bottom:25px;">Editor's Choice</h3>
                <inertia-link
                    :href="href"
                    class="text-grey-dark">
                    <div class="row space-y-4 md:space-y-0">
                        <div class="col-full md:col-1/4">
                            <div class="relative">
                                <div class="absolute z-10 top-0 left-0 px-2 "><div class="absolute inset-0 bg-grey-darkest opacity-75"></div> <div class="relative leading-relaxed text-white uppercase text-xs font-bold tracking-wide">
                                    {{ label }}
                                </div></div>
                                <div class="image-column relative">
                                    <img :src="editorchoice.video_data.thumbnail" class="w-full rounded" alt="Thumbnail" />
                                    <div class="absolute bottom-0 left-0 bg-white w-12 h-12 inline-flex justify-center items-center"><svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="play" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" class="svg-inline--fa fa-play fa-w-14"><path fill="currentColor" d="M424.4 214.7L72.4 6.6C43.8-10.3 0 6.1 0 47.9V464c0 37.5 40.7 60.1 72.4 41.3l352-208c31.4-18.5 31.5-64.1 0-82.6z" class=""></path></svg></div>
                                </div>
                            </div>
                        </div>
                        
                        <div class="col-full md:col-3/4 text-column">
                            <h4 class="font-semibold text-lg leading-tight mb-4">
                                {{ editorchoice.title }}
                                <LeftHandedIcon v-if="editorchoice.left_handed"></LeftHandedIcon>
                            </h4>
                            <div class="mb-4"><i>{{ authorList }}</i>
                                <p>
                                    {{ publishedAt }} |
                                    {{ duration }} mins |
                                    {{ categoryList }}
                                    <span v-if="editorchoice.expertise_level" style="text-transform:capitalize;">| {{ editorchoice.expertise_level }}</span>
                                    
                                </p>
                            </div>
                            
                            <div class="text-sm" v-html="editorchoice.summary"/>
                            
                            <!-- Quiz Button -->
                            <div v-if="showEditorChoiceQuizButton" class="mt-4">
                                <button
                                    type="button"
                                    @click.stop.prevent="goToQuiz"
                                    class="inline-block bg-red-600 hover:text-white hover:bg-red-400 text-white font-semibold py-2 px-4 rounded transition-colors">
                                    Test your knowledge
                                </button>
                            </div>
                            
                        </div>
                    </div>
                </inertia-link>
            </template>
            
            <div v-for="section in sections" class="pt-8 border-t border-t-grey-light">

                <inertia-link :href="`/categories/${section.slug}`" class="flex items-center mb-8 text-grey-dark">
                    <h3 class="w-48 mr-4 text-3xl font-light uppercase">{{ section.name }}</h3>
                    <font-awesome-icon class="text-primary hover:text-primary-dark" icon="chevron-right" size="lg"/>
                </inertia-link>

                <div class="row space-y-8 md:space-y-0">
                    <template v-for="(post, i) in section.posts">
                        <div class="md:col-1/2 flex flex-col">
                            <PostTile
                                :key="post.id"
                                :post="post"
                                class="mb-auto"/>
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script>

import Layout from "@app/Shared/Components/Layout";
import HasMetaData from "@app/Shared/Mixins/HasMetaData";
import PostThumbnail from "@app/Shared/Components/PostThumbnail";
import PostTile from "@app/Shared/Components/PostTile";
import LeftHandedIcon from "@app/Shared/Components/LeftHandedIcon";
import {PostType} from "@admin/Lib/Enums";
import dayjs from "dayjs";

export default {

    mixins: [HasMetaData],

    props: {
        page: Object,
        sections: Array,
        editorchoice: {
            type: null
        }
    },

    layout: Layout,

    components: {
        PostThumbnail,
        PostTile,
        LeftHandedIcon,
    },
    
    computed: {
        showEditorChoiceQuizButton() {
            if (!this.editorchoice || !this.editorchoice.publish_quiz) {
                return false;
            }
            return this.editorchoice.questions && this.editorchoice.questions.length > 0;
        },
        label() {
            switch (this.editorchoice.type) {
                case PostType.Tutorial:
                    return 'Tutorial'
                case PostType.CaseReport:
                    return 'Case Report'
            }
        },
        href() {
            switch (this.editorchoice.type) {
                case PostType.Tutorial:
                    return `/tutorial/${this.editorchoice.id}`
                case PostType.CaseReport:
                    return `/case-report/${this.editorchoice.id}`
            }
        },
        authorList() {

            const authors = this.editorchoice.authors;
            const length = authors.length;
            return authors.reduce((acc, curr, index, arr) => {
                if(index == length-1) {
                    return acc += `${curr.first_name} ${curr.last_name}`
                }else if(index == length-2) {
                    return acc += `${curr.first_name} ${curr.last_name} & `
                }else{
                    return acc += `${curr.first_name} ${curr.last_name}, `   
                }
            }, '');
        },
        publishedAt() {
            return dayjs(this.editorchoice.published_at).format('MMMM D, YYYY')
        },
        duration() {
            return Math.ceil(this.editorchoice.duration / 60);
        },
        expertiseLevel() {
            return _.startCase(this.editorchoice.expertise_evel);
        },
        categoryList() {
            return _.chain(this.editorchoice.categories)
                .groupBy('parent_id')
                .map(items => {
                    const parentName = items[0].parent?.name;
                    const names = items.map(item => item?.name).join('; ');

                    return `${parentName} (${names})`
                })
                .value()
                .join('|')
        }
    },
    methods: {
        goToQuiz() {
            window.location.href = `${this.href}?scroll=quiz`;
        }
    }
};

</script>



